import { SHEET_KEYS } from '../../../models/sheet-keys';
import {
  PureSheetValidationResult,
  SheetValidationContext,
  SheetValidator,
} from '../sheet-validator';
import { differsAtCentPrecision } from '../validation-field-key.util';
import {
  areRole18YearsConsecutive,
  buildRole18ColumnKey,
  extractRole18SchemaTaxonomy,
  getRole18EndReconciliationPair,
  isRole18CalculatedControl,
  isRole18Empty,
  isRole18MonetaryItem,
  isRole18SectionHeader,
  pickRole18Schema,
  readRole18ActiveFilingInfo,
  role18AccountingStandardToTaxonomy,
  Role18Schema,
  Role18SchemaControl,
} from './role18-cash-flows-indirect.rules';
import {
  resolveDimensionVisibility,
  readSlice,
  VisibilityCell,
} from './dimension-visibility.util';
import { getDimensionVisibilityConfig } from './dimension-visibility-registry';
import {
  accountingStandardToXbrlPrefix,
  calculator,
  hasCalculatedFieldMismatch,
  isResolvedAccountingStandard,
} from './calc-diff.helper';
import {
  recomputeReferencedSheets,
  referencedSheetKeysFromSchema,
} from '../cross-sheet-recompute.util';
import { pickRole8Schema } from './role8-statement-of-financial-position.rules';
import { pickRole12Schema } from './role12-statement-of-profit-or-loss.rules';

/**
 * Pure validator for Role 18 (Statement of Cash Flows, Indirect Method).
 *
 * Mirrors the editor's role18-520000-statement-of-cash-flows-indirect.component.ts
 * `evaluateSheetStatus()` surface — i.e. which rows resolve to the 'Failed'
 * mapping status in `getRowValidationStatus()` and thereby flip the persisted
 * `hasErrors` flag on `SheetLevelStatusData`. The component publishes:
 *
 *   this.stateService.updateSheetLevelStatus(
 *     'statementOfCashFlowsIndirectMethod',
 *     { hasErrors }
 *   );
 *
 * where `hasErrors = true` iff at least one schema row's
 * `getRowValidationStatus()` returns 'Failed'. The Failed branches are:
 *
 *  1. **Static required-field check** (`control.required === true`) — the
 *     editor's `validateRequiredFields()` walks every visible
 *     (dimension × period) cell of every required MonetaryItemType control
 *     and flags it when null/undefined/'' via `mandatoryFieldErrors` and a
 *     `{label}: The field is mandatory.` string in `validationErrors`.
 *     `getRowValidationStatus()` returns 'Failed' when
 *     `control.required === true && !hasValue` for any visible column.
 *     Calculated/readonly rows are skipped (the component's `buildForm()`
 *     only attaches `notNullValidator` when `control.required === true`, AND
 *     calculated rows are populated by CalculationService — flagging them
 *     would be a false-positive).
 *
 *  2. **Schema-declared equality (both cross-sheet and same-sheet)** — via
 *     `EqualityValidationService.validateAllEqualityRules()`. The role 18
 *     schemas declare equality rules at:
 *       * `ProfitLossBeforeTax` → Role 12 SOPL (`ifrs-full:ProfitLossBeforeTax`
 *         / `ifrs-smes:ProfitLossBeforeTax`).
 *       * (MPERS only) `ProfitLossFromDiscontinuedOperations` → Role 12 SOPL.
 *       * `CashAndBankBalances` → Role 8 SOFP (`ssmt:CashAndBankBalances`).
 *       * `CashAndCashEquivalents:End` ↔ `CashAndCashEquivalents:End_occurrence2`
 *         (same-sheet reciprocal chain with `skipIfSourceNull` /
 *         `skipIfTargetNull`).
 *     All are standard-equality (no `operation: 'sum'`), dualColumn, and
 *     carry cent precision
 *     it.
 *
 *  3. **Consecutive-year cash reconciliation** — via
 *     `CashFlowReconciliationService.validateConsecutiveYearCash()`. Gated by
 *     consecutive-year dates in filingPrerequisites. Compares
 *     `CashAndCashEquivalents:Beginning[*][CurrentYear]` against the
 *     previous-year End / End_occurrence2 balance and flags the row Failed
 *     when no comparison matches at cent precision.
 *
 *  4. **Same-sheet CashAndCashEquivalents End vs End_occurrence2
 *     reconciliation** — handled by the component's
 *     `validateCashAndCashEquivalentsEnd()` which pushes string errors into
 *     `validationErrors` (NOT into `equalityValidationErrors`). These
 *     strings do NOT contain the literal "does not match the extracted sum"
 *     so they satisfy `hasErrorCategory` and flip the row to Failed.
 *     Technically the same pair is already covered by the schema's
 *     `equality` blocks with `skipIfSourceNull` / `skipIfTargetNull`, but the
 *     component's bespoke check uses a `hasEndValue || hasEndOccurrence2Value`
 *     gate that differs slightly — we mirror the component's bespoke gate
 *     because `validateRequiredFields` runs BEFORE the schema-driven
 *     equality check and produces identical Failed outcomes regardless.
 *
 * Drivers intentionally NOT mirrored (would not flip `hasErrors`):
 *
 *  - API-mismatch warnings ("does not match the extracted sum") from
 *    `validateAgainstApiData()` — these resolve to 'Validated*', not 'Failed'.
 *  - `editedRows` / edit-mode tracking — resolves to 'Edited', not 'Failed'.
 *  - Form-control `invalid && (dirty || touched)` — unreachable in pure
 *    mode (no FormControl exists when validating a persisted payload).
 *
 * ---- Cross-sheet posture / dependency graph ------------------------------
 *
 * Per `SheetDependencyService`:
 *   statementOfCashFlowsIndirectMethod -> [statementOfFinancialPosition,
 *                                          statementOfProfitOrLoss]
 * (Role 18 invalidates Role 8 and Role 12.) Reverse edges also exist:
 *   statementOfFinancialPosition -> [statementOfChangesInEquity,
 *                                    statementOfCashFlowsIndirectMethod]
 *   statementOfProfitOrLoss      -> [statementOfCashFlowsIndirectMethod]
 * so Role 18 is in a cycle with both Role 8 and Role 12 (by design — the
 * three sheets publish totals that must agree).
 *
 * The validator reads `context.crossSheetData` defensively and tolerates
 * absent slices per the Tier 4 task contract. Each cross-sheet check
 * degrades gracefully:
 *
 *   • filing-info absent → Consolidated columns SHOWN (the shared resolver's
 *     unknown-Nature default, matching the mounted editor via
 *     `CrossReferenceService.getVisibility`; the former role-local strict
 *     `=== 'Consolidated'` default-to-hidden was retired in the
 *     dimension-visibility convergence), PreviousYear shown (unknown-prep
 *     default), consecutive-year check silently skipped.
 *   • Role 8 absent → CashAndBankBalances equality skipped (no flagging
 *     when there is nothing to compare against).
 *   • Role 12 absent → ProfitLossBeforeTax equality skipped.
 */

interface CrossSheetReader {
  read(sheet: string): Record<string, any> | undefined;
}

function makeCrossSheetReader(bundle: Record<string, Record<string, any>>): CrossSheetReader {
  return {
    read(sheet: string) {
      return bundle[sheet];
    },
  };
}

/**
 * Materialise the cross-sheet bundle (a `Map`) into a plain record and recompute
 * every referenced sheet's calculated totals BEFORE any equality compare — so
 * the headless path compares against the SAME recomputed operands the mounted
 * editor sees (filing-form-container recomputes every statement sheet at load).
 * Role 18's equality rules reference Role 8 (`ssmt:CashAndBankBalances`, itself
 * a Role-9-inherited leaf) and Role 12 (`ProfitLossBeforeTax`, a calculated
 * total); reading either raw was the false-`Success*` parity gap. Mirrors the
 * `recomputeReferencedSheets` step role 8 / role 15 already run.
 */
function buildRecomputedBundle(schema: Role18Schema, context: SheetValidationContext, taxonomy: 'MFRS' | 'MPERS'): Record<string, Record<string, any>> {
  const bundle: Record<string, Record<string, any>> = {};
  if (context.crossSheetData) {
    for (const [key, value] of context.crossSheetData) {
      bundle[key as string] = value as Record<string, any>;
    }
  }
  const referencedKeys = referencedSheetKeysFromSchema(schema, SHEET_KEYS.statementOfCashFlowsIndirectMethod);
  
  // Referenced operands (Role 12 ProfitLossBeforeTax, Role 8 cash) are
  // recompute-of-latest — never the N8N snapshot (revised global rule).
  return recomputeReferencedSheets(
    bundle,
    referencedKeys,
    taxonomy,
    (sheetKey) => schemaForReferencedSheet(sheetKey, context.accountingStandard)
  ) as Record<string, Record<string, any>>;
}

/**
 * Schema provider for `recomputeReferencedSheets`: returns the referenced
 * sheet's schema so the util can replay that sheet's own leaf inheritance
 * (Role 8 ← Role 9, Role 12 ← Role 13) before recomputing its totals. Only the
 * sheets Role 18 compares against need an entry; any other key recomputes from
 * its raw slice (no inheritance) or passes through.
 */
function schemaForReferencedSheet(sheetKey: string, accountingStandard: string | undefined): { controls?: any[] } | undefined {
  if (sheetKey === SHEET_KEYS.statementOfFinancialPosition) {
    return pickRole8Schema(accountingStandard);
  }
  if (sheetKey === SHEET_KEYS.statementOfProfitOrLoss) {
    return pickRole12Schema(accountingStandard);
  }
  return undefined;
}

/* ------------------------------------------------------------------------ */
/* LB-001 fix-guard: required-by-validators-array detection                 */
/* ------------------------------------------------------------------------ */

interface SchemaValidatorEntry {
  name?: string;
  conditionalOn?: unknown;
}

interface SchemaControlForRequired {
  required?: boolean;
  validators?: SchemaValidatorEntry[];
}

/**
 * LB-001 fix-guard: a control is statically required when EITHER
 * `required === true` OR the validators array contains an unconditional
 * `{ name: 'required' }` entry. Today neither role 18 schema declares a
 * `validators` array (both schemas only use the top-level `required: true`
 * flag on four fields: `ssmt:CashAndBankBalances`,
 * `CashAndCashEquivalents:Beginning`, `CashAndCashEquivalents:End`, plus
 * `ifrs-full:ProfitLossBeforeTax` is NOT required). Carrying the guard
 * means a future schema edit declaring requiredness via `validators: [{
 * name: 'required' }]` is picked up automatically — same posture as roles
 * 6, 12, 13, 21, 22.
 */
function hasUnconditionalRequiredViaValidators(
  control: SchemaControlForRequired
): boolean {
  return (
    control.validators?.some(
      (validator) => validator?.name === 'required' && validator?.conditionalOn === undefined
    ) ?? false
  );
}

function isRole18StaticallyRequired(control: Role18SchemaControl): boolean {
  return (
    control.required === true ||
    hasUnconditionalRequiredViaValidators(control as SchemaControlForRequired)
  );
}

/* ------------------------------------------------------------------------ */
/* (1) Static required-field check                                          */
/* ------------------------------------------------------------------------ */

/**
 * Mirrors `validateRequiredFields()` + `getRowValidationStatus()`'s
 * required-field branch: for every required MonetaryItemType control, every
 * visible (dimension × period) cell must carry a non-empty value. Empty →
 * row resolves to Failed. Calculated/readonly rows are skipped because the
 * component's `buildForm()` attaches `notNullValidator` from
 * `control.required === true` — and the schema intentionally does not mark
 * calculated rows required (flagging them would be a false positive since
 * CalculationService always populates them).
 *
 * Returns true on first missing cell — short-circuits because
 * `evaluateSheetStatus()` breaks on the first Failed row.
 */
function checkRequiredFieldErrors(
  schema: Role18Schema,
  sheetData: Record<string, any>,
  visibleColumns: ReadonlyArray<VisibilityCell>
): boolean {
  for (const control of schema.controls) {
    if (isRole18SectionHeader(control)) continue;
    if (!isRole18MonetaryItem(control)) continue;
    if (!isRole18StaticallyRequired(control)) continue;
    if (isRole18CalculatedControl(control)) continue;

    for (const column of visibleColumns) {
      if (!column.visible) continue;
      const key = buildRole18ColumnKey(
        control.qname,
        column.dimension,
        column.period
      );
      if (isRole18Empty(sheetData[key])) {
        return true;
      }
    }
  }
  return false;
}

/* ------------------------------------------------------------------------ */
/* (2) Schema-declared equality rules                                       */
/*     (cross-sheet: Role 8, Role 12; same-sheet: End ↔ End_occurrence2)    */
/* ------------------------------------------------------------------------ */

function coerceNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Mirrors the column-aware standard-equality branch of
 * `EqualityValidationService.validateAllEqualityRules()`. For each control
 * with a non-sum `validation.equality` block, compare the local value
 * against the referenced sheet's value across visible columns. A
 * mismatch at cent precision flips Failed.
 *
 * Lookup is always `qname[Dim][Period]` because all three target sheets
 * (role 18 self, role 8, role 12) are `dualColumn` per
 * validation-field-key.util's SHEET_COLUMN_TYPE_MAP.
 *
 * Conditional flags (`skipIfSourceNull`, `skipIfTargetNull`) are honoured
 * exactly — they are what lets the reciprocal End ↔ End_occurrence2 rules
 * avoid double-counting when only one side is populated. Both-null also
 * skips (matches the editor's "skip if both null" branch).
 */
function checkEqualityErrors(
  schema: Role18Schema,
  sheetData: Record<string, any>,
  reader: CrossSheetReader,
  visibleColumns: ReadonlyArray<VisibilityCell>,
  taxonomy: 'MFRS' | 'MPERS'
): boolean {
  for (const control of schema.controls) {
    if (isRole18SectionHeader(control)) continue;
    const rules = control.validation?.equality;
    if (!rules || rules.length === 0) continue;

    // Standard equality only — role 18 schemas don't declare sum rules.
    // If a future edit adds one, extend this function (flag in latent-bugs
    // ledger).
    const isSum = rules.every((rule) => rule.operation === 'sum');
    if (isSum) continue;

    for (const rule of rules) {
      if (
        rule.taxonomy &&
        rule.taxonomy.toUpperCase() !== taxonomy.toUpperCase()
      ) {
        continue;
      }

      // Source sheet data: same-sheet → use local sheetData; cross-sheet →
      // read from the bundle (tolerates absence — skip entirely rather than
      // flagging based on missing data).
      const isSameSheet = rule.sheet === SHEET_KEYS.statementOfCashFlowsIndirectMethod;
      const sourcePayload = isSameSheet ? sheetData : reader.read(rule.sheet);
      if (!isSameSheet && !sourcePayload) continue;

      for (const column of visibleColumns) {
        if (!column.visible) continue;

        const localKey = buildRole18ColumnKey(
          control.qname,
          column.dimension,
          column.period
        );
        const sourceKey = buildRole18ColumnKey(
          rule.field,
          column.dimension,
          column.period
        );

        const rawLocal = sheetData[localKey];
        const rawSource = sourcePayload?.[sourceKey];

        const localEmpty = isRole18Empty(rawLocal);
        const sourceEmpty = isRole18Empty(rawSource);

        // Skip when both null (nothing to compare).
        if (localEmpty && sourceEmpty) continue;

        // The same-sheet `End` ↔ `End_occurrence2` reciprocal is now compared
        // against the RECOMPUTED twin (the validator overlays
        // `calculateTotals_520000` onto the payload before this layer runs), so
        // the twin is always present — matching the mounted editor. The former
        // "skip when the twin is absent" stop-gap is gone; the both-null skip
        // above plus the schema's `skipIfSourceNull` / `skipIfTargetNull` flags
        // below are sufficient.

        // Honour the conditional-skip flags — critical for the End ↔
        // End_occurrence2 reciprocal chain.
        //
        // NOTE ON RULE SEMANTICS: the component's error-producing
        // branches in EqualityValidationService use `rawCurrentValue` and
        // `rawCompareValue` where "current" is the control carrying the
        // rule and "compare" is the referenced field. Mirror that exactly.
        if (rule.skipIfSourceNull && localEmpty) continue;
        if (rule.skipIfTargetNull && sourceEmpty) continue;

        const localNum = coerceNumber(rawLocal) ?? 0;
        const sourceNum = coerceNumber(rawSource) ?? 0;

        if (differsAtCentPrecision(localNum, sourceNum)) {
          return true;
        }
      }
    }
  }
  return false;
}

/* ------------------------------------------------------------------------ */
/* (3) Consecutive-year cash reconciliation                                 */
/* ------------------------------------------------------------------------ */

/**
 * Mirrors `CashFlowReconciliationService.validateConsecutiveYearCash()`. The
 * rule is anchored on the `CashAndCashEquivalents:Beginning` control via a
 * `validation.consecutiveYearCash` block. Semantics:
 *
 *   1. Gate on consecutive-year dates in filingPrerequisites. Absent / non-
 *      consecutive → rule silently skipped.
 *   2. For each consolidation type present in the comparisons list, the
 *      rule compares `Beginning[dim][CurrentYear]` against each
 *      `compareField[dim][PreviousYear]` candidate. If ANY comparison
 *      matches at cent precision, that consolidation passes (the editor's
 *      `anyMatch` flag). If NONE matches, the row is flagged Failed.
 *   3. Calculated/readonly compareField rows contribute `endNum = 0` when
 *      the cell is null — mirrors the editor's `if (endNum === null &&
 *      compareIsCalc) endNum = 0` line.
 *
 * Returns true on first failing consolidation type. `getRowValidationStatus`
 * flips the Beginning row to Failed via the `equalityValidationErrors` Map
 * entry the editor pushes, so any failing comparison is sufficient.
 */
function checkConsecutiveYearCashErrors(
  schema: Role18Schema,
  sheetData: Record<string, any>,
  context: SheetValidationContext
): boolean {
  // Phase-aware: read whichever filing-info slot is populated. See
  // `readRole18ActiveFilingInfo` for the rationale.
  const filingPrerequisites = readRole18ActiveFilingInfo(
    context.crossSheetData
  );
  if (!filingPrerequisites) return false;
  if (!areRole18YearsConsecutive(filingPrerequisites)) return false;

  for (const control of schema.controls) {
    if (isRole18SectionHeader(control)) continue;
    const rule = control.validation?.consecutiveYearCash;
    if (!rule) continue;

    // Group comparisons by consolidation type, as the editor does.
    const compsByConsolidation = new Map<
      string,
      Array<{ compareField: string }>
    >();
    for (const comp of rule.comparisons) {
      const key = comp.beginningConsolidationType;
      if (!compsByConsolidation.has(key)) {
        compsByConsolidation.set(key, []);
      }
      compsByConsolidation.get(key)!.push(comp);
    }

    for (const [consolidationType, comps] of compsByConsolidation) {
      const beginKey = `${control.qname}[${consolidationType}][CurrentYear]`;
      const beginRaw = sheetData[beginKey];
      const beginNum = coerceNumber(beginRaw);
      // Editor's short-circuit: null/NaN beginning → skip entirely.
      if (beginNum === null) continue;

      let anyMatch = false;
      let anyFailed = false;
      for (const comp of comps) {
        const compareControl = schema.controls.find(
          (control) => control.qname === comp.compareField
        );
        const compareIsCalc = compareControl
          ? isRole18CalculatedControl(compareControl)
          : false;

        const endKey = `${comp.compareField}[${consolidationType}][PreviousYear]`;
        const endRaw = sheetData[endKey];
        let endNum = coerceNumber(endRaw);
        if (endNum === null && compareIsCalc) endNum = 0;
        if (endNum === null) continue;

        // Cents equal = a real match. (The previous `diff <= tolerance` counted
        // a one-cent-off End as a match, suppressing a true mismatch — inverse of
        // the `> tolerance` bug. Beginning must equal SOME previous End exactly.)
        if (!differsAtCentPrecision(beginNum, endNum)) {
          anyMatch = true;
        } else {
          anyFailed = true;
        }
      }

      // Editor's condition: "Only report errors if no comparison matched
      // for this consolidation type."
      if (!anyMatch && anyFailed) {
        return true;
      }
    }
  }

  return false;
}

/* ------------------------------------------------------------------------ */
/* (4) Same-sheet End ↔ End_occurrence2 bespoke reconciliation              */
/* ------------------------------------------------------------------------ */

/**
 * Mirrors the component's `validateCashAndCashEquivalentsEnd()`. Compares
 * `CashAndCashEquivalents:End` against `CashAndCashEquivalents:End_occurrence2`
 * per visible column when at least one side has a non-zero value. Mismatch
 * at cent precision flags Failed for both rows.
 *
 * NOTE: the schema's `equality` blocks on the same pair (with
 * `skipIfSourceNull` / `skipIfTargetNull`) ALSO trigger a Failed via
 * `checkEqualityErrors`. In practice both paths produce identical Failed
 * decisions on any real mismatch — keeping this dedicated branch for
 * robustness against future schema edits that might drop the equality
 * blocks in favour of the bespoke check.
 */
function checkEndReconciliationErrors(
  sheetData: Record<string, any>,
  visibleColumns: ReadonlyArray<VisibilityCell>,
  accountingStandard: string | undefined | null
): boolean {
  const pair = getRole18EndReconciliationPair(accountingStandard);

  for (const column of visibleColumns) {
    if (!column.visible) continue;

    const endKey = buildRole18ColumnKey(
      pair.endQname,
      column.dimension,
      column.period
    );
    const endOccKey = buildRole18ColumnKey(
      pair.endOccurrence2Qname,
      column.dimension,
      column.period
    );

    const endRaw = sheetData[endKey];
    const endOccRaw = sheetData[endOccKey];

    // `CashAndCashEquivalents:End_occurrence2` is the frontend-only calculated
    // reciprocal twin. The backend `retrieve-mbrs-data` payload never emits it,
    // so the caller overlays `calculateTotals_520000` onto the payload before
    // this layer runs — the twin is therefore always present and recomputed
    // exactly as the editor's CalculationService computes it. The former
    // "skip when the twin is absent" stop-gap (which masked real mismatches like
    // an extracted End that does not reconcile with the details-derived end) is
    // gone; the `hasEndValue || hasEndOccValue` gate below still guards the
    // all-zero case.

    const endNum = coerceNumber(endRaw) ?? 0;
    const endOccNum = coerceNumber(endOccRaw) ?? 0;

    // Mirror the component's `hasEndValue || hasEndOccurrence2Value` gate:
    // require at least one side to have a non-null AND non-zero value to
    // trigger the check.
    const hasEndValue = !isRole18Empty(endRaw) && endNum !== 0;
    const hasEndOccValue = !isRole18Empty(endOccRaw) && endOccNum !== 0;
    if (!hasEndValue && !hasEndOccValue) continue;

    if (differsAtCentPrecision(endNum, endOccNum)) {
      return true;
    }
  }

  return false;
}

/* ------------------------------------------------------------------------ */
/* Public validator                                                         */
/* ------------------------------------------------------------------------ */

export const role18CashFlowsIndirectValidator: SheetValidator = {
  sheetKey: SHEET_KEYS.statementOfCashFlowsIndirectMethod,
  // Tier 4: cross-sheet reads (filingPrerequisites + role 8 + role 12) AND
  // schema-driven calculations participate in the Failed drivers. The
  // ProfitLossBeforeTax / CashAndBankBalances equality checks compare
  // calculation outputs across three sheets; the End ↔ End_occurrence2
  // reconciliation compares two calculation outputs on the same sheet.
  // Dual-path parity-harness mandatory in dev.
  tier: 4,
  validate(
    sheetData: Record<string, any>,
    context: SheetValidationContext
  ): PureSheetValidationResult {
    const schema = pickRole18Schema(context.accountingStandard);
    // Column visibility now comes from the shared, config-driven resolver
    // (dimension-visibility.util + registry) — the same one the mounted editor
    // calls via `CrossReferenceService.getVisibility`. The role-local
    // `resolveRole18ColumnVisibility` (strict `=== 'Consolidated'`, prereq-only)
    // was removed. The shared resolver is prereq-first with a filingInfo
    // fallback (the approved posture) and treats an unknown Nature as
    // "Consolidated shown" — consistent with roles 8 / 12 / 15 / 19.
    const visibleColumns = resolveDimensionVisibility(
      getDimensionVisibilityConfig(SHEET_KEYS.statementOfCashFlowsIndirectMethod)!,
      {
        filingPrerequisites: readSlice(context.crossSheetData, 'filingPrerequisites'),
        filingInfo: readSlice(context.crossSheetData, 'filingInfo'),
      }
    );

    // Strict-mode taxonomy agreement guard — mirrors
    // validateAllEqualityRules' "skip if schema taxonomy doesn't match"
    // branch. By construction pickRole18Schema returns a schema whose
    // taxonomy matches contextTaxonomy, but we keep the guard for
    // unrecognised accountingStandard values (same posture as role 8).
    const contextTaxonomy = role18AccountingStandardToTaxonomy(context.accountingStandard);
    const schemaTaxonomy = extractRole18SchemaTaxonomy(schema);
    const taxonomiesAgree = schemaTaxonomy !== null && schemaTaxonomy === contextTaxonomy;
    // ------------------
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    
    // Diagnostic logs — kept while we verify Phase 1/Phase 2 parity across
    // the parity-check path AND the dashboard listing path. Remove once both
    // paths agree across a representative sample of conversions.
    // const rawFilingPrerequisites = context.crossSheetData?.get('filingPrerequisites');
    // const rawFilingInfo = context.crossSheetData?.get('filingInfo');

    // console.log('[On Role 18 Cash Flow | Indirect | Validation]: Prerequisites slot: ', rawFilingPrerequisites);
    // console.log('[On Role 18 Cash Flow | Indirect | Validation]: FilingInfo slot: ', rawFilingInfo);
    // console.log('[On Role 18 Cash Flow | Indirect | Validation]: Active filing-info (resolved): ', filingPrerequisites);
    // console.log('[On Role 18 Cash Flow | Indirect | Validation]: Visible Columns: ', visibleColumns);
    // console.log('[On Role 18 Cash Flow | Indirect | Validation]: Data: ', sheetData);
    // console.log('[On Role 18 Cash Flow | Indirect | Validation]: Taxonomies Agree: ', taxonomiesAgree, '(context:', contextTaxonomy, ', schema:', schemaTaxonomy, ')');
    // console.log(
    //   '[On Role 18 Cash Flow | Indirect | Validation]: Has Required-Field Errors: ',
    //   checkRequiredFieldErrors(schema, sheetData, visibleColumns)
    // );
    // ------------------
    // Recompute role 18's OWN calculated totals up front and overlay them onto
    // the validation payload. The same-sheet End ↔ End_occurrence2 reconciliation
    // depends on `CashAndCashEquivalents:End_occurrence2` — a FRONTEND-ONLY
    // calculated twin (the "Details of cash flows" end-of-period =
    // CashAndBankBalances − BankOverdraft + OtherAdjustments) that the backend
    // `retrieve-mbrs-data` payload never emits. The mounted editor's
    // CalculationService always fills it (via the same `calculateTotals_520000`),
    // so on a freshly-converted, never-opened sheet the headless path must
    // recompute it to match — otherwise the twin is absent and the reciprocal is
    // silently skipped, publishing a green `Success*` for a sheet the editor
    // shows as a red cross (the false-`Success*` parity gap). `calculateTotals_520000`
    // only writes calculated rows (the twin, the running totals) and never the
    // extracted `:End` / `ProfitLossBeforeTax` / `CashAndBankBalances` operands,
    // so the overlay leaves every extracted value intact. (This supersedes the
    // former "skip when the twin is absent" stop-gaps that masked real mismatches.)
    const recomputed = calculator.calculateTotals_520000(
      sheetData,
      accountingStandardToXbrlPrefix(context.accountingStandard)
    );
    const computedSheetData: Record<string, any> = { ...sheetData, ...recomputed };

    // Layer 1: static required-field check (component's
    // validateRequiredFields + getRowValidationStatus's required branch).
    // Runs on the RAW payload — required cells are extracted, never calculated,
    // so the overlay is irrelevant here.
    if (checkRequiredFieldErrors(schema, sheetData, visibleColumns)) {
      return { hasErrors: true, hasSuggestedChanges: false };
    }

    // Layers 2-4 only fire when taxonomies agree (matches
    // validateAllEqualityRules' short-circuit + validateConsecutiveYearCash's
    // same guard). They run on `computedSheetData` so the recomputed End twin
    // (and any other calculated operand) is compared exactly as the editor sees it.
    if (taxonomiesAgree) {
      // Layer 2: schema-declared equality rules (cross-sheet + same-sheet).
      // Build the reader over the RECOMPUTED cross-sheet bundle so the cross-
      // sheet operands (Role 8 cash, Role 12 ProfitLossBeforeTax) match the
      // editor's recomputed values, not the raw N8N extraction.
      const reader = makeCrossSheetReader(
        buildRecomputedBundle(schema, context, contextTaxonomy)
      );
      if (checkEqualityErrors(schema, computedSheetData, reader, visibleColumns, contextTaxonomy)) {
        return { hasErrors: true, hasSuggestedChanges: false };
      }

      // Layer 3: consecutive-year cash reconciliation.
      if (checkConsecutiveYearCashErrors(schema, computedSheetData, context)) {
        return { hasErrors: true, hasSuggestedChanges: false };
      }

      // Layer 4: bespoke End ↔ End_occurrence2 reconciliation.
      if (checkEndReconciliationErrors(computedSheetData, visibleColumns, context.accountingStandard)) {
        return { hasErrors: true, hasSuggestedChanges: false };
      }
    }

    // AS-842 calc-diff: surface `Validated*` in the silent path. With no
    // errors found above, diff role 18's recomputed calculated totals against
    // the persisted (N8N-extracted) values; any mismatch flips
    // `hasSuggestedChanges`, which the resolver rolls up to `Success*`.
    return {
      hasErrors: false,
      hasSuggestedChanges:
        isResolvedAccountingStandard(context.accountingStandard) &&
        hasCalculatedFieldMismatch(context.extractedBaseline ?? sheetData, recomputed),
    };
  },
};
